import React, {useState, useEffect, useCallback} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {useSubscription} from '@/shared/utils'
import {listTokens, createToken, deleteToken, ApiKey} from './api'

const EXPIRY_OPTIONS = [
  {label: '30 days', value: 30},
  {label: '90 days', value: 90},
  {label: '180 days', value: 180},
  {label: '1 year', value: 365},
]

const formatDate = (epochSeconds?: number | null): string =>
  epochSeconds
    ? new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—'

/**
 * "Engine API Keys" settings card. Lets a user mint and revoke Personal Access
 * Tokens used to authenticate the headless OmicsBox engine (CLI) — e.g. on a
 * multi-node cluster — without an interactive login. Self-gates: renders only
 * when the active subscription licenses ENGINE, or the user already has keys.
 */
export const ApiKeysSection: React.FC = () => {
  const {linkedSubscriptions, subscriptionId, subscriptionDetails} = useSubscription()
  const hasEngine = subscriptionDetails?.modules?.includes('ENGINE') ?? false
  const [keys, setKeys] = useState<ApiKey[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(90)
  const [isCreating, setIsCreating] = useState(false)
  // The plaintext key is returned only once (on create) — held here to reveal
  // in a modal with a copy button, then discarded.
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<{id: string; name: string} | null>(null)

  const fetchKeys = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await listTokens()
      setKeys(data.keys || [])
    } catch (err) {
      getLogger().error('Failed to load API keys:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchKeys()
  }, [fetchKeys])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return

    setIsCreating(true)
    try {
      // Bind the key to the currently-active subscription (falls back to the
      // account default server-side if none is selected).
      const res = await createToken(trimmed, expiresInDays, subscriptionId || undefined)
      setCreatedKey(res.apiKey)
      setCopied(false)
      setName('')
      // Prepend the new key's metadata so it shows immediately without a refetch.
      setKeys((prev) => [
        {
          id: res.id,
          name: res.name,
          tier: res.tier,
          subscriptionId: res.subscriptionId,
          scopes: res.scopes,
          createdAt: res.createdAt,
          expiresAt: res.expiresAt,
          lastUsedAt: null,
          revoked: false,
        },
        ...(prev || []),
      ])
      toast.success('API key created', toastDefaultConfig)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create API key'
      toast.error(msg, toastDefaultConfig)
      getLogger().error('Create API key failed:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCopy = async () => {
    if (!createdKey) return
    try {
      await navigator.clipboard.writeText(createdKey)
      setCopied(true)
    } catch (err) {
      getLogger().error('Clipboard copy failed:', err)
    }
  }

  const confirmDelete = async () => {
    if (!confirmRevoke) return
    const {id} = confirmRevoke
    setConfirmRevoke(null)
    setDeletingId(id)
    try {
      await deleteToken(id)
      toast.success('API key revoked', toastDefaultConfig)
      // Mark revoked locally — the activeKeys filter hides the row.
      setKeys((prev) => (prev ? prev.map((k) => (k.id === id ? {...k, revoked: true} : k)) : prev))
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to revoke API key'
      toast.error(msg, toastDefaultConfig)
      getLogger().error('Revoke API key failed:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const activeKeys = (keys || []).filter((k) => !k.revoked)

  // Resolve a key's subscription to a friendly label + expired flag by matching
  // its bound subscriptionId against the user's linked subscriptions. A key whose
  // subscription is not among them is treated as expired/unlinked (expired
  // non-primary subscriptions are dropped from /v1/user/subscriptions server-side).
  const subscriptionInfo = (
    key: ApiKey
  ): {id: string; product: string | null; expired: boolean} => {
    if (!key.subscriptionId) return {id: '—', product: null, expired: false}
    const sub = (linkedSubscriptions || []).find(
      (s) => s.subscription_id === key.subscriptionId || s.skey === key.subscriptionId
    )
    if (!sub) return {id: key.subscriptionId, product: null, expired: true}
    return {id: key.subscriptionId, product: sub.product, expired: sub.is_expired}
  }

  // Show the card when the active subscription licenses ENGINE, or the user still
  // has keys from another / now-inactive subscription to manage.
  if (!hasEngine && activeKeys.length === 0) return null

  return (
    <div className='col-12 col-lg-6'>
      <div className='ob-page__section d-flex flex-column flex-grow-1'>
      <h3 className='ob-page__section-title'>
        <i className='fas fa-key me-2' />
        Engine API Keys
      </h3>
      <p className='ob-text--muted ob-text--small ob-spacing--mb-4'>
        Personal keys to authenticate the OmicsBox engine (CLI) without an interactive login — for
        example on a multi-node cluster. Pass a key as the <code>OMICSBOX_API_KEY</code> environment
        variable. Each key is scoped to your own account.
      </p>

      {/* Create form */}
      <div className='card mb-4'>
        <div className='card-body'>
          <h5 className='mb-3'>Create a key</h5>
          <form onSubmit={handleCreate} className='d-flex gap-2 flex-wrap'>
            <input
              type='text'
              className='form-control'
              style={{minWidth: '180px', flex: 1}}
              placeholder='Key name (e.g. laptop, hpc-cluster)'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
              maxLength={60}
            />
            <select
              className='form-select'
              style={{width: 'auto'}}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              disabled={isCreating}
              aria-label='Key expiry'
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type='submit'
              className='btn btn-bioabm'
              disabled={isCreating || !name.trim()}
              style={{whiteSpace: 'nowrap'}}
            >
              {isCreating ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  />
                  Creating...
                </>
              ) : (
                <>
                  <i className='fas fa-plus me-2' />
                  Create key
                </>
              )}
            </button>
          </form>
          {subscriptionId && (
            <div className='form-text mt-2'>
              New keys run against your active subscription
              {subscriptionDetails?.productName ? ` — ${subscriptionDetails.productName}` : ''}. Switch
              the active subscription above to create a key for a different one.
            </div>
          )}
        </div>
      </div>

      {/* Keys table */}
      <div className='card'>
        <div className='card-body'>
          <h5 className='mb-3'>Your keys</h5>
          {isLoading ? (
            <div className='text-center py-4'>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              />
              Loading keys...
            </div>
          ) : activeKeys.length === 0 ? (
            <p className='text-muted text-center py-3'>
              No keys yet. Create one to run the OmicsBox engine headlessly.
            </p>
          ) : (
            <div className='table-responsive ob-settings-table'>
              <table className='table table-hover align-middle mb-0' style={{fontSize: '0.9rem'}}>
                <thead style={{backgroundColor: 'var(--ob-gray-50)'}}>
                  <tr>
                    <th style={{padding: '0.5rem'}}>Name</th>
                    <th style={{padding: '0.5rem'}}>Subscription</th>
                    <th style={{padding: '0.5rem'}}>Created</th>
                    <th style={{padding: '0.5rem'}}>Expires</th>
                    <th style={{padding: '0.5rem'}}>Last used</th>
                    <th style={{padding: '0.5rem', textAlign: 'center', width: '80px'}}>Revoke</th>
                  </tr>
                </thead>
                <tbody>
                  {activeKeys.map((key) => (
                    <tr key={key.id}>
                      <td style={{padding: '0.5rem'}}>
                        <span className='fw-semibold'>
                          {key.name || <span className='text-muted'>Unnamed</span>}
                        </span>
                        {key.tier === 'test' && (
                          <span className='badge bg-secondary ms-2'>test</span>
                        )}
                      </td>
                      <td style={{padding: '0.5rem'}}>
                        <code
                          style={{fontSize: '0.85em'}}
                          title={subscriptionInfo(key).product || undefined}
                        >
                          {subscriptionInfo(key).id}
                        </code>
                        {subscriptionInfo(key).expired && (
                          <span
                            className='badge bg-warning text-dark ms-2'
                            title='The subscription this key runs against has expired — its runs will be rejected until the subscription is renewed.'
                          >
                            Linked subscription expired
                          </span>
                        )}
                      </td>
                      <td style={{padding: '0.5rem'}}>{formatDate(key.createdAt)}</td>
                      <td style={{padding: '0.5rem'}}>{formatDate(key.expiresAt)}</td>
                      <td style={{padding: '0.5rem'}}>
                        {key.lastUsedAt ? (
                          formatDate(key.lastUsedAt)
                        ) : (
                          <span className='text-muted'>Never</span>
                        )}
                      </td>
                      <td style={{padding: '0.5rem', textAlign: 'center'}}>
                        <button
                          className='btn btn-sm btn-outline-danger'
                          onClick={() =>
                            setConfirmRevoke({id: key.id, name: key.name || 'this key'})
                          }
                          disabled={deletingId === key.id}
                          title='Revoke key'
                        >
                          {deletingId === key.id ? (
                            <span
                              className='spinner-border spinner-border-sm'
                              role='status'
                              aria-hidden='true'
                            />
                          ) : (
                            <i className='fas fa-trash-alt' />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* One-time key reveal */}
      {createdKey && (
        <CustomModal
          title='Copy your new API key'
          closeModal={() => setCreatedKey(null)}
          buttons={[
            {text: 'Done', className: 'btn btn-primary', onClick: () => setCreatedKey(null)},
          ]}
        >
          <div>
            <p className='mb-2'>
              This is the only time the full key is shown. Copy it now and store it securely — you
              cannot retrieve it again.
            </p>
            <div className='d-flex align-items-center gap-2'>
              <code
                className='flex-grow-1 text-break p-2 bg-light rounded'
                style={{fontSize: '0.85em', wordBreak: 'break-all'}}
              >
                {createdKey}
              </code>
              <button
                className='btn btn-sm btn-outline-primary flex-shrink-0'
                onClick={handleCopy}
                title='Copy to clipboard'
              >
                <i className={`fas fa-${copied ? 'check' : 'copy'} me-1`} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className='text-muted small mt-3 mb-0'>
              Use it with the OmicsBox engine, e.g. <code>OMICSBOX_API_KEY=&lt;key&gt;</code>.
            </p>
          </div>
        </CustomModal>
      )}

      {/* Revoke confirmation */}
      {confirmRevoke && (
        <CustomModal
          title='Revoke API key'
          closeModal={() => setConfirmRevoke(null)}
          buttons={[
            {text: 'Cancel', className: 'btn btn-light', onClick: () => setConfirmRevoke(null)},
            {text: 'Revoke', className: 'btn btn-danger', onClick: confirmDelete},
          ]}
        >
          <p className='mb-0'>
            Revoke <strong>{confirmRevoke.name}</strong>? Any engine or CLI using this key will
            immediately stop working. This cannot be undone.
          </p>
        </CustomModal>
      )}
      </div>
    </div>
  )
}
